"use client"

import { useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { signIn } from "next-auth/react"
import { useRouter } from "next/navigation"
import {
  ArrowRight,
  Eye,
  EyeOff,
  Gamepad2,
  LoaderCircle,
  LockKeyhole,
  Mail,
} from "lucide-react"
import { Header } from "@/components/layout/header"
import { TurnstileWidget } from "@/components/security/turnstile-widget"
import styles from "./login-page.module.css"

export default function LoginPage() {
  const router = useRouter()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState("")
  const [turnstileResetKey, setTurnstileResetKey] = useState(0)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError("")
    setLoading(true)

    try {
      const result = await signIn("credentials", {
        email,
        password,
        turnstileToken,
        redirect: false,
      })

      if (result?.error) {
        setError("Verification expired or the email and password were invalid")
        setTurnstileToken("")
        setTurnstileResetKey((key) => key + 1)
      } else {
        router.push("/play")
        router.refresh()
      }
    } catch {
      setError("Something went wrong. Please try again.")
      setTurnstileToken("")
      setTurnstileResetKey((key) => key + 1)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`public-site ${styles.page}`}>
      <Header />

      <main className={styles.stage}>
        <div className={styles.ambientGlow} aria-hidden="true" />

        <div className={styles.leftVisual} aria-hidden="true">
          <span className={styles.visualHalo} />
          <Image
            src="/images/auth/login-trophy.webp"
            alt=""
            width={520}
            height={612}
            sizes="260px"
          />
        </div>

        <div className={styles.rightVisual} aria-hidden="true">
          <span className={styles.visualHalo} />
          <Image
            src="/images/auth/login-console.webp"
            alt=""
            width={760}
            height={973}
            sizes="360px"
          />
        </div>

        <section className={styles.authContent} aria-labelledby="login-title">
          <header className={styles.intro}>
            <h1 id="login-title">
              Welcome back.
              <span>
                Ready to <em>play?</em>
              </span>
            </h1>
            <p>
              Sign in to keep your scores, achievements, and longest streaks
              together.
            </p>
          </header>

          <div className={styles.mobileVisuals} aria-hidden="true">
            <Image
              src="/images/auth/login-trophy.webp"
              alt=""
              width={520}
              height={612}
              sizes="84px"
            />
            <Image
              src="/images/auth/login-console.webp"
              alt=""
              width={760}
              height={973}
              sizes="112px"
            />
          </div>

          <div className={styles.formCard}>
            <span className={styles.formMark} aria-hidden="true">
              <Gamepad2 />
            </span>

            <form onSubmit={handleSubmit} className={styles.form}>
              {error ? (
                <div role="alert" className={styles.errorMessage}>
                  {error}
                </div>
              ) : null}

              <div className={styles.field}>
                <label htmlFor="email">Email</label>
                <div className={styles.inputShell}>
                  <Mail aria-hidden="true" />
                  <input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label htmlFor="password">Password</label>
                <div className={styles.inputShell}>
                  <LockKeyhole aria-hidden="true" />
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="current-password"
                    required
                  />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={() => setShowPassword((visible) => !visible)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <EyeOff aria-hidden="true" />
                    ) : (
                      <Eye aria-hidden="true" />
                    )}
                  </button>
                </div>
                <Link
                  href="/forgot-password"
                  className={styles.forgotPassword}
                >
                  Forgot password?
                </Link>
              </div>

              <div className={styles.turnstileWrap}>
                <TurnstileWidget
                  onTokenChange={setTurnstileToken}
                  resetKey={turnstileResetKey}
                  theme="light"
                />
              </div>

              <button
                type="submit"
                className={styles.submitButton}
                disabled={loading || !turnstileToken}
              >
                <span>{loading ? "Signing in..." : "Sign in"}</span>
                <i aria-hidden="true">
                  {loading ? (
                    <LoaderCircle className={styles.spinner} />
                  ) : (
                    <ArrowRight />
                  )}
                </i>
              </button>
            </form>

            <p className={styles.accountPrompt}>
              Don&apos;t have an account?{" "}
              <Link href="/register">Create one</Link>
            </p>
          </div>
        </section>
      </main>
    </div>
  )
}
